import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import { Outlet } from "react-router-dom";
import { useMediaQuery } from "@mui/material";
import AppBar from "views/Components/AppBar";
import SideMenu from "views/Components/SideMenu";
import { darkTheme, lightTheme } from "services/utils";
import localization from "services/localization";
import SpecificAPI from "services/SpecificApi";
import { useUserStore } from "context/useUserStore";
import { useThemeStore } from "context/useThemeStore";
import { useWebSocket } from "context/useWebsocketStore";
import LoadingSpinner from "views/Components/LoadingSpinner";
import { useLanguageStore } from "context/useLanguageStore";

const SidebarLayout = () => {
  const { moduleId, departmentId, historyId } = useParams();
  const { theme } = useThemeStore();

  const isTablet = useMediaQuery("(max-width: 1024px)");
  const [collapsed, setCollapsed] = useState(isTablet);
  // const [theme, setTheme] = useState(
  //   localStorage.getItem("SA_user_theme") || "light",
  // );
  // const [darkMode, setDarkMode] = useState(theme === "dark");
  const [language, setLanguage] = useState(
    localStorage.getItem("SA_user_lang") || "En",
  );
  const [selectedHistoryId, setSelectedHistoryId] = useState(null);
  const [folderList, setFolderList] = useState([]);
  const [department, setDepartment] = useState(null);
  const [histories, setHistories] = useState([]);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { appUser, userData, isAppUserLoading, setUserAppData } =
    useUserStore();
  const { subscribe, leave, isReady } = useWebSocket();
  const { currentLang, setCurrentLang } = useLanguageStore();
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const updateNow = () => setNow(new Date());

    updateNow();
    const current = new Date();
    const delay =
      60000 - (current.getSeconds() * 1000 + current.getMilliseconds());

    let interval;

    const timeout = setTimeout(() => {
      updateNow();
      interval = setInterval(updateNow, 60000);
    }, delay);
    return () => {
      clearTimeout(timeout);
      if (interval) clearInterval(interval);
    };
  }, []);

  // useEffect(() => {
  //   document.documentElement.setAttribute("data-theme", theme);
  // }, [theme]);

  useEffect(() => {
    getAllData();
  }, [appUser]);

  useEffect(() => {
    if (historyId) {
      setSelectedHistoryId(historyId);
    }
  }, [historyId, departmentId]);

  useEffect(() => {
    if (selectedHistoryId !== null) {
      getHistoryDetail(selectedHistoryId);
    }
  }, [histories, selectedHistoryId]);

  const getAllData = async () => {
    if (!appUser) {
      return;
    }
    try {
      if (appUser?.role != "root") {
        const historyParams =
          appUser.role === "student"
            ? { department_id: departmentId, privacy: "public" }
            : { department_id: departmentId, user_id: appUser.id };
        setLoading(true);
        const historyResponse = await SpecificAPI.getAllHistory(
          historyParams,
          userData?.selected_school_id,
        );
        if (department) {
          setDepartment(department);
        }

        setHistories(historyResponse);
      }
    } finally {
      setLoading(false);
    }
  };

  // const pullHistory = async () => {
  //   if (!appUser || appUser.role != "student") {
  //     return;
  //   }
  //   try {
  //     const historyResponse = await SpecificAPI.getAllHistory(
  //       { department_id: departmentId, privacy: "public" },
  //       userData?.selected_school_id,
  //     );
  //     setHistories(historyResponse);
  //   } catch (err) {
  //     console.error("Failed to pull messages:", err);
  //   }
  // };

  // const getHistory = async () => {
  //   try {
  //     const historyResponse = await SpecificAPI.getAllHistory(
  //       { department_id: departmentId, privacy: "public" },
  //       userData?.selected_school_id,
  //     );
  //     setHistories(historyResponse);
  //   } catch (err) {
  //     console.error("Failed to pull messages:", err);
  //   }
  // };

  useEffect(() => {
    if (!isReady || !appUser) return;

    subscribe(`user.${appUser.id}`, ".history.created", (data) => {
      console.log("new history received : ", data);
      // var historyListData = Object.assign([], histories);
      // historyListData.push(data.history);
      // setHistories(historyListData);
      setHistories((prev) => [data.history, ...prev]);
    });

    subscribe(`user.${appUser.id}`, ".history.updated", (data) => {
      console.log("updated history received : ", data);
      setHistories((prev) =>
        prev.map((h) => (h.id === data.history.id ? data.history : h)),
      );
      setUserAppData({
        ...userData,
        updatedHistory: {
          id: data.history.id,
          datetime_start_playground:
            data.history?.prompt_data?.datetime_start_playground,
          datetime_end_playground:
            data.history?.prompt_data?.datetime_end_playground,
        },
      });
    });

    return () => leave(`user.${appUser.id}`);
  }, [isReady, appUser]);

  const toggleSidebar = () => setCollapsed(!collapsed);

  // const handleDarkModeChange = (e) => {
  //   const checked = e.target.checked;
  //   const mode = checked ? "dark" : "light";
  //   document.documentElement.setAttribute("data-theme", mode);
  //   localStorage.setItem("SA_user_theme", mode);
  //   setTheme(mode);
  //   setDarkMode(checked);
  // };

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    localStorage.setItem("SA_user_lang", lang);
    localization.setLanguage(lang);
    setCurrentLang(lang);
    window.dispatchEvent(new Event("storage"));
  };

  const loadHistory = async (historyId, classroomId) => {
    setSelectedHistoryId(historyId);
    if (classroomId) {
      try {
        const classroom = await SpecificAPI.getClassRoomById(classroomId);
        setDepartment(classroom);
      } catch (err) {
        console.error("Failed to load classroom:", err);
      }
    }
  };
  const getHistoryDetail = (historyId) => {
    const role = appUser?.role || null;
    if (role == "teacher" || role == "administrative") {
      navigate(
        `/edu-prompt/history/module/${moduleId}/${departmentId}/${historyId}`,
      );
    } else {
      navigate(`/edu-prompt/history/department/${departmentId}/${historyId}`);
    }
  };
  const getFolderMessages = (folderId) => {
    return new Promise((resolve) => {
      const filteredHistory = histories.filter((h) => h.folderId === folderId);
      resolve(filteredHistory);
    });
  };

  const handleNewMessage = () => {
    //add to api new history
    var historyListData = Object.assign([], histories);

    var newChat = {
      folderId: null,
      title: "New Conversation",
      messages: [],
      userId: "1",
    };
    return new Promise(function (resolve, reject) {
      SpecificAPI.createHistory(newChat, userData?.selected_school_id)
        .then((result) => {
          var data = result.data;
          historyListData.push[data];
          loadHistory(data.id);
          setHistories(historyListData);
          resolve(true);
        })
        .catch((error) => {
          console.error("Error retrieving history detail: ", error);
          reject(error);
        });
    });
  };

  const handleSavedMessages = () => {
    // return new Promise(function (resolve, reject) {
    //     SpecificAPI.getUserSavedMessage("1")
    //         .then((result) => {
    //             var messages = result.data;
    //             setMessages(messages);
    //             resolve(true);
    //         }).catch((error) => {
    //             console.error("Error retrieving history detail: ", error);
    //             reject(error);
    //         })
    // })
  };

  if (isAppUserLoading || !appUser) {
    return <LoadingSpinner />;
  }

  return (
    <ThemeProvider theme={theme === "dark" ? darkTheme : lightTheme}>
      <div className="d-flex">
        <SideMenu
          collapsed={collapsed}
          toggleSidebar={toggleSidebar}
          setCollapsed={setCollapsed}
          handleNewMessage={handleNewMessage}
          handleSavedMessages={handleSavedMessages}
          loadHistory={loadHistory}
          selectedHistory={selectedHistoryId}
          getFolderMessages={getFolderMessages}
          historyList={histories || []}
          folderList={folderList || []}
          theme={theme}
          moduleId={moduleId}
          departmentId={departmentId}
          setHistories={setHistories}
          loading={loading}
          now={now}
          setNow={setNow}
        />

        <div className="main-content flex-1 flex flex-col">
          <AppBar
            collapsed={collapsed}
            handleLanguageChange={handleLanguageChange}
            language={language}
          />
          <main
            className="flex-1 overflow-y-auto"
            style={{ paddingTop: "65px" }}
          >
            <Outlet />
          </main>
        </div>
      </div>
    </ThemeProvider>
  );
};

export default SidebarLayout;
